import { ref, watch, type Ref, type WatchSource } from "vue";

interface TimeLeft {
  seconds: Ref<number>;
  minutes: Ref<number>;
  hours: Ref<number>;
  days: Ref<number>;
  months: Ref<number>;
  years: Ref<number>;
}

export function useCountdown(futureDate: WatchSource<string>): {
  timeLeft: TimeLeft;
  clearCountdown: () => void;
} {
  const timeLeft: TimeLeft = {
    seconds: ref(0),
    minutes: ref(0),
    hours: ref(0),
    days: ref(0),
    months: ref(0),
    years: ref(0),
  };

  let interval: number | ReturnType<typeof window.setInterval> | undefined;
  const localFutureDate = ref<Date>(new Date()); // initializer doesnt matter
  const clearCountdown = () => {
    clearInterval(interval);
  };

  const zeroAll = () => {
    timeLeft.seconds.value = 0;
    timeLeft.minutes.value = 0;
    timeLeft.hours.value = 0;
    timeLeft.days.value = 0;
    timeLeft.months.value = 0;
    timeLeft.years.value = 0;
  };

  const calculateTimeLeft = () => {
    const parsed = Date.parse(localFutureDate.value.toString());
    // Guard against invalid dates (NaN from `Date.parse`) — without this,
    // every derived field becomes NaN and the consumer renders
    // "NaN days : NaN hours …" instead of falling through to the
    // start-soon copy.
    if (Number.isNaN(parsed)) {
      clearCountdown();
      zeroAll();
      return timeLeft;
    }

    const timeRemaining = parsed - Date.now();
    if (timeRemaining <= 0) {
      clearCountdown();
      zeroAll();
      return timeLeft;
    }

    timeLeft.seconds.value = Math.floor((timeRemaining / 1000) % 60);
    timeLeft.minutes.value = Math.floor((timeRemaining / 1000 / 60) % 60);
    timeLeft.hours.value = Math.floor((timeRemaining / (1000 * 60 * 60)) % 24);
    timeLeft.days.value = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    timeLeft.months.value = Math.floor(timeLeft.days.value / 30);
    timeLeft.years.value = Math.floor(timeLeft.months.value / 12);

    return timeLeft;
  };

  watch(
    futureDate,
    (newDateNum) => {
      if (!newDateNum) return;
      clearCountdown();
      localFutureDate.value = new Date(Number(newDateNum));
      interval = setInterval(calculateTimeLeft, 1000);
      calculateTimeLeft();
    },
    { immediate: true },
  );

  return {
    timeLeft,
    clearCountdown,
  };
}
